/**
 * The catalog as the API serves it and the browser reads it.
 *
 * Nothing here knows what the shop sells. A product has a category, a brand,
 * a price, stock, measurements and a bag of named properties; every filter
 * and every facet is derived from those rows at query time.
 */
import { z } from "zod";
export const Slug = {
    schema: z
        .string()
        .trim()
        .max(120)
        .regex(/^[a-z0-9-]+$/, "Not a valid slug.")
        .brand(),
};
export const ProductId = {
    schema: z.uuid().brand(),
};
export const CategoryId = {
    schema: z.uuid().brand(),
};
export const BrandId = {
    schema: z.uuid().brand(),
};
export const ImageId = {
    schema: z.uuid().brand(),
};
export const ProductSort = {
    NAMES: ["relevance", "price_asc", "price_desc", "rating", "newest"],
    schema: z.enum(["relevance", "price_asc", "price_desc", "rating", "newest"]),
    label: {
        relevance: "Relevance",
        price_asc: "Price: low to high",
        price_desc: "Price: high to low",
        rating: "Top rated",
        newest: "Newest",
    },
};
/** Free text as a person typed it, trimmed and without control characters. */
export const SearchText = {
    MAX_LENGTH: 120,
    // eslint-disable-next-line no-control-regex
    CONTROL: /[\x00-\x1f\x7f]/,
    schema: z
        .string()
        .trim()
        .max(120)
        .refine((text) => !SearchText.CONTROL.test(text), "Control characters are not allowed."),
};
/** A key repeated in a query string arrives as an array; a single one as a string. */
/** Text that reaches Postgres, which refuses the NUL character in any text value. */
export const StoredText = {
    hasNoNul: (text) => !text.includes("\u0000"),
    NUL_MESSAGE: "The NUL character is not allowed.",
    /** The length Postgres's `char_length` gives: code points, so an emoji counts once. */
    lengthOf: (text) => [...text].length,
};
/** A whole number compared with a Postgres `integer` column, which holds at most this. */
export const StoredInteger = {
    MAX: 2_147_483_647,
};
export const MultiValue = {
    schema: z
        .union([
        z.string().max(120).refine(StoredText.hasNoNul, StoredText.NUL_MESSAGE),
        z.array(z.string().max(120).refine(StoredText.hasNoNul, StoredText.NUL_MESSAGE)).max(50),
    ])
        .transform((value) => (typeof value === "string" ? [value] : value)),
    /** The same, where every value has to be a slug. */
    slugs: z.union([Slug.schema, z.array(Slug.schema).max(50)]).transform((value) => (typeof value === "string" ? [value] : value)),
};
export const ProductQueryParams = {
    schema: z
        .object({
        q: SearchText.schema.optional(),
        category: Slug.schema.optional(),
        brand: MultiValue.slugs.optional(),
        priceMin: z.coerce.number().int().min(0).max(StoredInteger.MAX).optional(),
        priceMax: z.coerce.number().int().min(0).max(StoredInteger.MAX).optional(),
        rating: z.coerce.number().int().min(1).max(5).optional(),
        sort: ProductSort.schema.default("relevance"),
        page: z.coerce.number().int().min(1).max(10_000).default(1),
        perPage: z.coerce.number().int().min(1).max(60).default(12),
    })
        .catchall(MultiValue.schema)
        .refine((params) => Object.keys(params).filter((key) => key.startsWith(ProductQuery.PROPERTY_PREFIX)).length <= ProductQuery.MAX_PROPERTY_FILTERS, "Use at most 20 property filters.")
        // A property's name comes from its key, which reaches Postgres too.
        .refine((params) => Object.keys(params).every(StoredText.hasNoNul), StoredText.NUL_MESSAGE),
};
export const ProductQuery = {
    DEFAULT_PER_PAGE: 12,
    MAX_PER_PAGE: 60,
    /** Query keys that start with this filter on a product property of that name. */
    PROPERTY_PREFIX: "p.",
    /** The most property filters one search takes, since each one is a subquery of every statement the search runs. */
    MAX_PROPERTY_FILTERS: 20,
    /** The query a parsed query string asks for. Empty text and empty lists count as absent. */
    fromParams: (params) => {
        const properties = [];
        for (const [key, value] of Object.entries(params)) {
            if (!key.startsWith(ProductQuery.PROPERTY_PREFIX) || !Array.isArray(value))
                continue;
            const name = key.slice(ProductQuery.PROPERTY_PREFIX.length);
            const values = value.filter((item) => item.length > 0);
            if (name.length > 0 && values.length > 0)
                properties.push({ name, values });
        }
        const brands = (params.brand ?? []).filter((slug) => slug.length > 0);
        return {
            ...(params.q !== undefined && params.q.length > 0 ? { text: params.q } : {}),
            ...(params.category !== undefined ? { category: params.category } : {}),
            brands,
            ...(params.priceMin !== undefined ? { priceMinCents: params.priceMin } : {}),
            ...(params.priceMax !== undefined ? { priceMaxCents: params.priceMax } : {}),
            ...(params.rating !== undefined ? { minRating: params.rating } : {}),
            properties,
            sort: params.sort,
            page: params.page,
            perPage: params.perPage,
        };
    },
};
export const SuggestParams = {
    schema: z.object({
        q: SearchText.schema.pipe(z.string().min(1).max(60)),
        limit: z.coerce.number().int().min(1).max(20).default(8),
    }),
};
export const Rating = {
    /** An average as a card shows it: rounded to one decimal. */
    shown: (average) => Math.round(average * 10) / 10,
    /** The rating of `count` approved reviews whose average is `average`. */
    of: (average, count) => (count === 0 ? { count } : { average: Rating.shown(average), count }),
};
export const Weight = {
    GRAMS_PER_OUNCE: 28.349523125,
    fromGrams: (grams) => ({
        grams,
        ounces: Math.round((grams / Weight.GRAMS_PER_OUNCE) * 100) / 100,
    }),
};
export const Dimensions = {
    MILLIMETERS_PER_INCH: 25.4,
    toInches: (millimeters) => Math.round((millimeters / Dimensions.MILLIMETERS_PER_INCH) * 100) / 100,
    fromMillimeters: (millimeters) => ({
        millimeters,
        inches: {
            width: Dimensions.toInches(millimeters.width),
            height: Dimensions.toInches(millimeters.height),
            depth: Dimensions.toInches(millimeters.depth),
        },
    }),
};
/** A price in the shop's one currency, kept as whole cents. */
export const Price = {
    CURRENCY: "EUR",
    format: (cents) => new Intl.NumberFormat("en", { style: "currency", currency: Price.CURRENCY }).format(cents / 100),
};
//# sourceMappingURL=catalog.js.map